import { cn } from '@/lib/utils';

function TreePrefix({ depth, isLast, ancestorLast }) {
  if (depth === 0) return null;
  return (
    <span className="inline-flex shrink-0 select-none font-mono text-slate-600" aria-hidden>
      {ancestorLast.slice(1).map((last, i) => (
        <span key={i} className="w-4 inline-block">{last ? ' ' : '│'}</span>
      ))}
      <span className="w-4 inline-block">{isLast ? '└─' : '├─'}</span>
    </span>
  );
}

const TYPE_COLORS = {
  bullet: 'text-violet-200 border-violet-500/30 bg-violet-950/40',
  explanation: 'text-sky-200 border-sky-500/30 bg-sky-950/40',
  image: 'text-emerald-200 border-emerald-500/30 bg-emerald-950/40',
  code_panel: 'text-amber-200 border-amber-500/30 bg-amber-950/40',
  scene_title: 'text-slate-200 border-slate-500/30 bg-slate-800/40',
  default: 'text-slate-300 border-slate-600/40 bg-slate-900/40',
};

function typeStyle(t) {
  return TYPE_COLORS[t] || TYPE_COLORS.default;
}

function BlockRow({ block, depth, isLast, ancestorLast }) {
  const t = (block.type || 'block').toLowerCase();
  const label = block.content || block.title || block.id || t;
  const narr = (block.narration || '').trim();
  const level = block.list_level ?? depth;

  return (
    <div className="min-w-0">
      <div className="flex items-start gap-0.5 py-0.5">
        <TreePrefix depth={depth} isLast={isLast} ancestorLast={ancestorLast} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={cn('text-[10px] px-1 py-0 rounded border uppercase', typeStyle(t))}>
              {t}
            </span>
            {level > 0 ? (
              <span className="text-[10px] text-slate-500">L{level}</span>
            ) : null}
            {block.artifact_type ? (
              <span className="text-[10px] px-1 py-0 rounded border border-violet-500/30 text-violet-200 bg-violet-950/40">
                {block.artifact_type}
              </span>
            ) : null}
            <span className="text-sm text-slate-200 line-clamp-2">{label}</span>
          </div>
          {narr ? (
            <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 pl-0.5">
              🎙 {narr}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders timeline.blocks as an indented teaching outline.
 */
export function TimelineBlocksOutline({ timeline, visualSkips, visualMermaidFallbacks }) {
  const blocks = (timeline?.blocks || []).filter((b) => (b.type || '') !== 'scene_transition');
  const skips = visualSkips || timeline?.visual_generation_skips || [];
  const mermaidFallbacks = visualMermaidFallbacks || timeline?.visual_mermaid_fallbacks || [];

  if (!blocks.length) {
    return <p className="text-sm text-slate-500">No timeline blocks yet — run timeline generation.</p>;
  }

  const scenes = timeline?.scenes?.length || 1;
  const meta = timeline?.courseMetadata || {};

  return (
    <div>
      {mermaidFallbacks.length > 0 ? (
        <div className="mb-3 rounded-sm border border-emerald-500/35 bg-emerald-500/10 px-3 py-2">
          <p className="text-xs font-semibold text-emerald-200">
            {mermaidFallbacks.length} diagram{mermaidFallbacks.length === 1 ? '' : 's'} created via Mermaid
            {timeline?.visual_generation_quota_exhausted ? ' (image quota exhausted)' : ''}
          </p>
          <ul className="mt-1.5 space-y-1 max-h-32 overflow-y-auto">
            {mermaidFallbacks.map((row, idx) => (
              <li key={row.block_id || idx} className="text-[11px] text-emerald-100/80">
                <span className="text-emerald-400/90 uppercase text-[10px]">mermaid</span>
                {row.content_preview ? ` · ${row.content_preview}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {skips.length > 0 ? (
        <div className="mb-3 rounded-sm border border-amber-500/35 bg-amber-500/10 px-3 py-2">
          <p className="text-xs font-semibold text-amber-200">
            {skips.length} image{skips.length === 1 ? '' : 's'} skipped
            {timeline?.visual_generation_quota_exhausted ? ' — provider quota exhausted' : ''}
          </p>
          <ul className="mt-1.5 space-y-1 max-h-32 overflow-y-auto">
            {skips.map((row, idx) => (
              <li key={row.block_id || idx} className="text-[11px] text-amber-100/80">
                <span className="text-amber-400/90 uppercase text-[10px]">{row.reason || 'skipped'}</span>
                {row.content_preview ? ` · ${row.content_preview}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="text-xs text-slate-500 mb-3">
        {blocks.length} content block(s)
        {meta.courseTitle ? ` · ${meta.courseTitle}` : ''}
        {scenes > 1 ? ` · ${scenes} scene(s)` : ''}
      </p>
      <div className="font-mono text-[12px] leading-relaxed space-y-0.5">
        {blocks.map((block, idx) => {
          const level = Math.max(0, Number(block.list_level) || 0);
          return (
            <BlockRow
              key={block.id || `b-${idx}`}
              block={block}
              depth={level + 1}
              isLast={idx === blocks.length - 1}
              ancestorLast={Array(level + 1).fill(false).map((_, i) => i === level)}
            />
          );
        })}
      </div>
    </div>
  );
}
